/**
 * Reaction Target
 *
 * Connects the shared reaction bar to persisted reaction targets.
 *
 * Primary responsibilities:
 * - Fetch and mutate reaction counts for one target
 * - Refresh counts when another participant reacts in realtime
 *
 * Dependencies:
 * - Reaction API route
 * - Supabase realtime browser client
 */

'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ReactionBar,
  type ReactionEmoji,
  type ReactionSummary
} from '@/components/reactions/reaction-bar'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { loadReactionsBatched } from '@/lib/reactions/batch-loader'
import { subscribeToReactionTarget } from '@/lib/realtime/reaction-subscriptions'
import { getBrowserSupabaseClient } from '@/lib/supabase/browser-client'
import { cn } from '@/lib/utils'

export type ReactionTargetType =
  | 'message'
  | 'space_conversation_message'
  | 'space_task_comment'

type ApiReactionSummary = {
  emoji: string
  count: number
  reactedByUser: boolean
}

type ReactionTargetProps = {
  targetType: ReactionTargetType | null | undefined
  targetId: string | null | undefined
  className?: string
  disabled?: boolean
  showPicker?: boolean
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isApiReaction(value: unknown): value is ApiReactionSummary {
  return (
    isRecord(value) &&
    typeof value.emoji === 'string' &&
    typeof value.count === 'number' &&
    typeof value.reactedByUser === 'boolean'
  )
}

function readReactions(payload: unknown): ReactionSummary[] {
  if (!isRecord(payload) || !Array.isArray(payload.reactions)) {
    return []
  }

  return payload.reactions
    .filter(isApiReaction)
    .map(reaction => ({
      emoji: reaction.emoji,
      count: reaction.count,
      reactedByCurrentUser: reaction.reactedByUser
    }))
}

function sortReactions(reactions: ReactionSummary[]) {
  return [...reactions]
    .filter(reaction => reaction.count > 0)
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count
      }

      return left.emoji.localeCompare(right.emoji)
    })
}

function updateReactionCount(
  reactions: ReactionSummary[],
  emoji: ReactionEmoji,
  delta: number,
  reactedByCurrentUser: boolean
) {
  const next = [...reactions]
  const index = next.findIndex(reaction => reaction.emoji === emoji)

  if (index >= 0) {
    next[index] = {
      ...next[index],
      count: Math.max(0, next[index].count + delta),
      reactedByCurrentUser
    }
    return next
  }

  if (delta <= 0) {
    return next
  }

  return [
    ...next,
    {
      emoji,
      count: delta,
      reactedByCurrentUser
    }
  ]
}

function applyOptimisticReaction(
  reactions: ReactionSummary[],
  emoji: ReactionEmoji
) {
  const activeReaction =
    reactions.find(reaction => reaction.reactedByCurrentUser) ?? null

  if (activeReaction?.emoji === emoji) {
    return sortReactions(updateReactionCount(reactions, emoji, -1, false))
  }

  const withoutPrevious = activeReaction
    ? updateReactionCount(reactions, activeReaction.emoji, -1, false)
    : reactions

  return sortReactions(updateReactionCount(withoutPrevious, emoji, 1, true))
}

// Loads go through the batch loader (lib/reactions/batch-loader):
// every bar mounted in the same commit shares ONE /api/reactions/batch
// request instead of firing its own. The loader keeps the flood guards
// from the danieldisu6 fix — in-flight dedupe and a 30s per-target
// backoff after a transport failure — so a dead target still cannot
// rejoin every realtime refresh wave.

// --------------

export function ReactionTarget({
  targetType,
  targetId,
  className,
  disabled = false,
  showPicker = true
}: ReactionTargetProps) {
  const [reactions, setReactions] = useState<ReactionSummary[]>([])
  const [isMutating, setIsMutating] = useState(false)
  const latestRequestRef = useRef(0)
  const canUseTarget = Boolean(
    targetType && targetId && UUID_PATTERN.test(targetId)
  )

  const loadReactions = useCallback(
    async (signal: AbortSignal) => {
      if (!canUseTarget || !targetId || !targetType) return

      const requestId = latestRequestRef.current + 1
      latestRequestRef.current = requestId

      // Batched: all bars in the same commit share one request.
      // Null means skipped (backoff) or failed — keep last counts;
      // realtime refreshes once the network is back.
      const summaries = await loadReactionsBatched(
        { targetType, targetId },
        { signal }
      )

      if (!summaries || signal.aborted) return
      if (latestRequestRef.current !== requestId) return

      setReactions(
        sortReactions(
          summaries.map(reaction => ({
            emoji: reaction.emoji,
            count: reaction.count,
            reactedByCurrentUser: reaction.reactedByUser
          }))
        )
      )
    },
    [canUseTarget, targetId, targetType]
  )

  useEffect(() => {
    if (!canUseTarget) {
      setReactions([])
      return
    }

    const controller = new AbortController()
    void loadReactions(controller.signal)

    return () => controller.abort()
  }, [canUseTarget, loadReactions])

  useEffect(() => {
    if (!canUseTarget || !targetId || !targetType) return

    let disposed = false
    const controller = new AbortController()
    const supabase = getBrowserSupabaseClient()
    let unsubscribe: (() => void) | null = null

    void subscribeToReactionTarget({
      onChange: () => {
        if (!disposed) {
          void loadReactions(controller.signal)
        }
      },
      supabase,
      targetId,
      targetType
    }).then(nextUnsubscribe => {
      if (disposed) {
        nextUnsubscribe()
        return
      }

      unsubscribe = nextUnsubscribe
    })

    return () => {
      disposed = true
      controller.abort()
      unsubscribe?.()
    }
  }, [canUseTarget, loadReactions, targetId, targetType])

  const handleToggleReaction = useCallback(
    async (emoji: ReactionEmoji) => {
      if (!canUseTarget || !targetId || !targetType || isMutating) return

      const previousReactions = reactions
      const activeReaction =
        reactions.find(reaction => reaction.reactedByCurrentUser) ?? null
      const action =
        activeReaction && activeReaction.emoji !== emoji ? 'switch' : 'toggle'

      setIsMutating(true)
      setReactions(applyOptimisticReaction(reactions, emoji))

      try {
        const response = await authenticatedFetch('/api/reactions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action,
            emoji,
            previousEmoji: activeReaction?.emoji ?? null,
            targetId,
            targetType
          })
        })

        if (!response.ok) {
          setReactions(previousReactions)
          return
        }

        setReactions(sortReactions(readReactions(await response.json())))
      } catch {
        setReactions(previousReactions)
      } finally {
        setIsMutating(false)
      }
    },
    [canUseTarget, isMutating, reactions, targetId, targetType]
  )

  if (!canUseTarget) {
    return null
  }

  return (
    <ReactionBar
      className={cn('mt-2', className)}
      disabled={disabled || isMutating}
      onToggleReaction={handleToggleReaction}
      reactions={reactions}
      showPicker={showPicker}
    />
  )
}
